import React from 'react';
import {
	Alert,
	Box,
	Button,
	List,
	ListItem,
	ListItemText,
	Typography,
} from '@mui/material';
import Modal from './Modal';
import { EXTRA_SEAT_MONTHLY, PRO_INCLUDED_SEATS } from '../../Shared/pricing';

const formatUsd = (amount) =>
	amount == null ? null : `$${Number(amount).toFixed(2)}`;

const formatProration = (preview) => {
	if (preview?.prorated_amount_cents == null) {
		return null;
	}
	const dollars = Math.abs(preview.prorated_amount_cents) / 100;
	const label = preview.prorated_credit ? 'Prorated credit' : 'Due today';
	return `${label}: ${formatUsd(dollars)}`;
};

const SeatBillingConfirmModal = ({
	open,
	onClose,
	onConfirm,
	title,
	description,
	preview,
	confirmLabel = 'Confirm and continue',
	loading = false,
	children,
}) => {
	const recurringDelta = preview?.recurring_delta_monthly_usd ?? 0;
	const prorationLabel = formatProration(preview);
	const prepaidUnlimitedSeats = Boolean(preview?.prepaid_unlimited_seats);
	const extraSeat = preview?.extra_seat_monthly_usd ?? EXTRA_SEAT_MONTHLY;
	const extraFrom = (preview?.included_seats ?? PRO_INCLUDED_SEATS) + 1;

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle={title}
			maxWidth="sm"
			useActionButtons
			actionButtons={
				<>
					<Button onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button variant="contained" onClick={onConfirm} disabled={loading}>
						{loading ? 'Processing...' : confirmLabel}
					</Button>
				</>
			}
		>
			<Box sx={{ mt: 1 }}>
				{description ? (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						{description}
					</Typography>
				) : null}

				{children}

				<List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
					{preview?.target_seat_count != null ? (
						<ListItem>
							<ListItemText
								primary="Seats after change"
								secondary={`${preview.target_seat_count} active seats`}
							/>
						</ListItem>
					) : null}
					{preview?.target_billable_extra_seats > 0 ? (
						<ListItem>
							<ListItemText
								primary={
									prepaidUnlimitedSeats
										? `Extra seats to be billed after prepaid term (${extraFrom}+)`
										: `Billable extra seats (${extraFrom}+)`
								}
								secondary={`${preview.target_billable_extra_seats} × $${extraSeat}/mo`}
							/>
						</ListItem>
					) : null}
					{recurringDelta !== 0 ? (
						<ListItem>
							<ListItemText
								primary={
									prepaidUnlimitedSeats
										? 'Monthly subscription change (starts after prepaid)'
										: 'Monthly subscription change'
								}
								secondary={`${recurringDelta > 0 ? '+' : ''}${formatUsd(recurringDelta)}/mo`}
							/>
						</ListItem>
					) : null}
					{prorationLabel ? (
						<ListItem>
							<ListItemText primary={prorationLabel} />
						</ListItem>
					) : null}
				</List>

				{prepaidUnlimitedSeats ? (
					<Alert severity="info" sx={{ mb: 1 }}>
						Extra seat charges start when your 3-month prepaid term ends. No
						immediate charge is expected today.
					</Alert>
				) : recurringDelta > 0 || prorationLabel ? (
					<Alert severity="info" sx={{ mb: 1 }}>
						Your subscription will update immediately. Stripe may charge or
						credit a prorated amount for the remainder of this billing period.
					</Alert>
				) : null}
			</Box>
		</Modal>
	);
};

export default SeatBillingConfirmModal;
